import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import ViewEditDialog from "../../../../utils/ui-components/ViewEditDialog";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MainCard from "../../../../utils/ui-components/MainCard";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddUser from "./add-user";
import { useLocation, useNavigate } from "react-router-dom";
import { UserService } from "../../../../assets/_services/user-service";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextFieldWrapper from "../../../../utils/ui-components/FormsUI/TextField";
import { useDispatch } from "react-redux";
import { MENU_OPEN, SET_BREADCRUMBS } from "../../../../redux/actions/actions";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { DataGrid, GridAlignment } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../../utils/ui-components/EmptyResultDataGrid";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { RateCardService } from "../../../../assets/_services/rate-card-service";
import UpdateParentUser from "./update-user";

export interface UserAddValues {
  id: string | number;
  companyName: string;
  companyContact: string;
  username: string;
  password: string;
  confirmPassword: string;
  status: boolean;
  allowSecondApprover: boolean;
  rateCardId: string | number;
  assignChannelList: string[] | number[];
  firstApprover: number | string;
  secondApprover: number | string;
}

function ParentUserList(props: {
  value: number | string;
  // approvers: { firstApprovers: { value: number | string; label: string }[]; secondApprovers: { value: number | string; label: string }[] };
}) {
  const INITIAL_FORM_STATE: UserAddValues = {
    id: "",
    companyName: "",
    companyContact: "",
    username: "",
    password: "",
    confirmPassword: "",
    status: true,
    assignChannelList: [],
    rateCardId: "",
    firstApprover: "",
    secondApprover: "",
    allowSecondApprover: false,
  };

  const theme: any = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  let { state } = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState(state?.search || "");
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const [paginationModel, setPaginationModel] = useState(
    state?.paginationModel || {
      page: 0,
      pageSize: 10,
    }
  );
  const [count, setCount] = useState(state?.count || 0);

  const [open, setOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [initialItem, setInitialItem] = useState<UserAddValues>(INITIAL_FORM_STATE);
  const [rateCards, setRateCards] = useState<{ value: number | string; label: string }[]>([]);
  const [firstApprovers, setFirstApprover] = useState<{ value: number | string; label: string }[]>([]);
  const [secondApprovers, setSecondApprover] = useState<{ value: number | string; label: string }[]>([]);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState<number | string>("");

  const path = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
    window.history.replaceState({}, "");
    getAllRateCards();
    // getAllChannels();
    dispatch({
      type: SET_BREADCRUMBS,
      payload: [
        { name: "Home", path: "/Dashboard", bold: true, state: null },
        { name: "User Management", path: null, bold: false, state: null },
        { name: "Parent Users", path: null, bold: false, state: null },
      ],
    });
    fetchFirstApproverList();
    fetchSecondApproverList();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchApiUserList(search);
  }, [paginationModel]);

  const fetchApiUserList = (searchValue: string) => {
    setIsLoading(true);
    let formData = {
      userName: searchValue,
    };
    UserService.getAllParentList(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setUsers(response.data.data.rows);
        setCount(response.data.data.count);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const getAllRateCards = () => {
    RateCardService.getRateCardListWithoutPagination().then((response) => {
      if (response.isSuccess) {
        setRateCards(
          response.data.data.map((item: any) => {
            return {
              label: item.rate_card_name,
              value: item.id,
            };
          })
        );
      }
    });
  };

  const fetchFirstApproverList = () => {
    let formData = {
      role: 3,
    };
    UserService.getApproverListWithoutPagination(formData).then((response) => {
      if (response.isSuccess) {
        setFirstApprover(
          response.data.data.map((item: any) => ({
            value: item.id,
            label: `${item.userName} - ( ${item.companyName} )`,
          }))
        );
      } else {
        setFirstApprover([]);
      }
    });
  };

  const fetchSecondApproverList = () => {
    let formData = {
      role: 4,
    };
    UserService.getApproverListWithoutPagination(formData).then((response) => {
      if (response.isSuccess) {
        setSecondApprover(
          response.data.data.map((item: any) => ({
            value: item.id,
            label: `${item.userName} - ( ${item.companyName} )`,
          }))
        );
      } else {
        setSecondApprover([]);
      }
    });
  };

  const handleClickOpen = (dialogTitle: string, formState: UserAddValues) => {
    setOpen(true);
    setDialogTitle(dialogTitle);
    setInitialItem(formState);
  };

  const dialog = useMemo(
    () =>
      ViewEditDialog(initialItem?.id ? UpdateParentUser : AddUser)({
        open: open,
        setOpen: setOpen,
        dialogTitle: dialogTitle,
        initialItem: initialItem,
        fetchData: () => {
          fetchApiUserList(search);
        },
        theme: theme,
        maxWidth: "sm",
        initialData: {
          rateCards: rateCards,
          firstApprovers: firstApprovers,
          secondApprovers: secondApprovers,
        },
      }),
    [open]
  );

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      width: 80,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <Stack direction={"row"}>
            <Tooltip title="Create API User">
              <IconButton
                aria-label="view"
                color="primary"
                onClick={() => {
                  navigate("/api-user/list", { state: { search: search, paginationModel: paginationModel, parentData: { ...params.row } } });
                }}
              >
                <GroupAddIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                aria-label="view"
                color="primary"
                onClick={() => {
                  setIsLoadingUserDetails(params.row?.id);
                  UserService.getParentDetailsById({ ownerId: params.row?.id }).then((response) => {
                    if (response.isSuccess) {
                      setIsLoadingUserDetails("");
                      handleClickOpen(`Update Parent User`, {
                        id: params.row?.id,
                        companyContact: params.row?.companyContact || "",
                        companyName: params.row?.companyName || "",
                        confirmPassword: "",
                        password: "",
                        status: params.row?.status ? true : false,
                        username: params.row?.userName || "",
                        rateCardId: response.data.data?.rateCardId || "",
                        firstApprover: response.data.data?.first_level_approval,
                        secondApprover: response.data.data?.second_level_approval || "",
                        allowSecondApprover: response.data.data?.second_level_approval ? true : false,
                        assignChannelList: response.data.data?.assignedChannelList.split(",").map((num: any) => ({ value: Number(num) })) || [],
                      });
                    } else {
                      setIsLoadingUserDetails("");
                    }
                  });
                }}
              >
                {isLoadingUserDetails === params.row?.id ? <CircularProgress size={"20px"} /> : <VisibilityIcon color="primary" />}
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      headerName: "ID",
      sortable: false,
      disableExport: false,
      maxWidth: 100,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "companyName",
      filterable: false,
      headerName: "Company Name",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "companyContact",
      filterable: false,
      headerName: "Company Contact",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
      // renderCell: (params: any) => {
      //   return <Typography>{params.row.companyContact}</Typography>;
      // },
    },
    {
      field: "userName",
      filterable: false,
      headerName: "Username",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "firstLevelApproverName",
      filterable: false,
      headerName: "First Approver",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "secondLevelApproverName",
      filterable: false,
      headerName: "Second Approver",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      sortable: false,
      disableExport: false,
      width: 120,
      align: "center" as GridAlignment,
      renderCell: (params: any) => {
        return params?.row?.status ? (
          <Chip variant="outlined" color="success" label="Active" />
        ) : (
          <Chip variant="outlined" color="error" label="Inactive" />
        );
      },
    },
    {
      field: "rate_card_name",
      filterable: false,
      headerName: "Rate Card",
      sortable: false,
      disableExport: false,
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Formik
          initialValues={{
            userName: state?.search || "",
          }}
          validationSchema={Yup.object().shape({
            userName: Yup.string()
              .notRequired()
              .matches(/^[a-zA-Z0-9_]*$/, "Username can contain only letters, numbers, and underscores"),
          })}
          onSubmit={(values: any, { setSubmitting }) => {
            setPaginationModel({ ...paginationModel, page: 0 });
            setSearch(values.userName || "");
            fetchApiUserList(values.userName);
          }}
        >
          {() => (
            <Form>
              <Grid container columnSpacing={1} justifyContent={"flex-end"}>
                <Grid item>
                  <TextFieldWrapper
                    label="Search"
                    placeholder="Username.."
                    name="userName"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: "250px" }}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item>
                  <Button color="primary" variant="contained" sx={{ ...theme.typography.customInput }} type="submit">
                    View
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    type="button"
                    sx={{ ...theme.typography.customInput }}
                    disabled={open}
                    onClick={() => handleClickOpen(`Add Parent User`, INITIAL_FORM_STATE)}
                    startIcon={<AddIcon />}
                  >
                    Create User
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
      <Grid item lg={open ? 7 : 12} md={open ? 7 : 12} sm={12} xs={12}>
        <DataGrid
          autoHeight
          columns={columns}
          loading={isLoading}
          rows={users}
          slots={{
            noRowsOverlay: EmptyResultDataGrid,
            loadingOverlay: () => <LinearProgress />,
          }}
          style={{ overflow: "auto" }}
          sx={{
            "& .MuiDataGrid-cell.stickyColumn": {
              position: "sticky",
              left: 0,
              backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
              zIndex: 1,
            },
            "& .clicked-row": {
              backgroundColor: `${theme.palette.mode === "light" ? theme.palette.secondary.light : theme.palette.background.footer} !important`,
            },
            cursor: "pointer",
          }}
          getRowClassName={(params) => (params.id === initialItem.id ? "clicked-row" : "")}
          pagination
          getRowId={(row: any) => row.id}
          paginationMode="server"
          rowCount={count}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </Grid>
      {open && !matchDownSM ? (
        <Grow in={open && !matchDownSM} timeout={200}>
          <Grid item lg={5} md={5}>
            <MainCard title={dialogTitle}>
              {initialItem?.id ? (
                <UpdateParentUser
                  initialItem={initialItem}
                  setOpen={setOpen}
                  fetchData={() => {
                    fetchApiUserList(search);
                  }}
                  initialData={{
                    rateCards: rateCards,
                    firstApprovers: firstApprovers,
                    secondApprovers: secondApprovers,
                  }}
                />
              ) : (
                <AddUser
                  initialItem={initialItem}
                  setOpen={setOpen}
                  fetchData={() => {
                    fetchApiUserList(search);
                  }}
                  initialData={{
                    rateCards: rateCards,
                    firstApprovers: firstApprovers,
                    secondApprovers: secondApprovers,
                  }}
                />
              )}
            </MainCard>
          </Grid>
        </Grow>
      ) : (
        dialog
      )}
    </Grid>
  );
}

export default ParentUserList;
